import { mapGetters } from 'vuex';

import events from '@/services/events';

export default {
  data() {
    return {
      hasInitiated: false
    };
  },

  computed: {
    ...mapGetters('favoriteOrders', ['favoriteOrders']),
    ...mapGetters('locations', ['selectedLocation']),
    ...mapGetters('menu', ['menu']),
    ...mapGetters('merchant', ['merchant']),
    ...mapGetters('orders', ['order']),
    ...mapGetters('offers', ['offers']),
    ...mapGetters('session', ['currentUser', 'orderDetails', 'session']),
    ...mapGetters('modals', ['currentlyOpenModal']),

    isOrderAheadLoading() {
      return [
        !this.hasInitiated,
        this.merchant.isLoading,
        this.menu.loadStatus === 'loading',
        this.order.isLoading,
        this.selectedLocation.isLoading,
        this.currentUser.isLoading
      ].some(Boolean);
    }
  },

  watch: {
    'selectedLocation.storeId': 'handlePickupLocationOrderType',
    currentlyOpenModal: 'modalWatcher'
  },

  methods: {
    async initializeOrderAhead() {
      events.on('group-ordering:fulfilled', this.groupOrderFulfilledHandler);

      await this.$store.dispatch('session/load');

      await this.$store.dispatch('locations/getLocationConditionally', { withMenu: true });

      this.$store.dispatch('orders/normalizeOrderDetails', {
        defaultFulfillmentType: 'pick-up',
        defaultOrderType: 'standard',
        orderTypes: ['standard', 'group', 'catering']
      });

      this.handlePickupLocationOrderType();

      this.$store.dispatch('menu/getMenu');

      this.$store.dispatch('orders/getOrder');

      this.checkIsPointClaimQuery();

      this.checkIsPromoCodeClaimQuery();

      this.checkIsRetroClaimQuery();

      setTimeout(() => {
        /*
          NOTE: This delay is necessary to allow our Vuex actions to initiate and
          update loading states before attempting to render components (<router-view>).
          Otherwise, components will be rendered twice (once on app start, and again when
          loading states resolve).
        */

        this.hasInitiated = true;
      }, 25);
    },

    async checkIsPointClaimQuery() {
      if (this.$route.query.pgcc) {
        this.$store.commit('offers/SET_GIFTING_POINTS_DATA', {
          pointGiftClaimCode: this.$route.query.pgcc,
          currencyPluralName: this.$route.query.currencyPluralName
        });

        this.$router.push({
          query: {
            pgcc: undefined,
            currencyPluralName: undefined,
            utm_source: undefined
          }
        });

        if (this.currentUser.accountId) {
          this.$store.commit('offers/SET_GIFTING_POINTS_DATA', { claimGiftedPointsFlow: 'SIGNED_IN' });

          this.$openModal('claimGiftedPointsModal');
        }
        else {
          this.$openModal('createAccount');
        }
      }
    },

    async checkIsPromoCodeClaimQuery() {
      if (this.$route.query.claimCode && this.$route.query.claimType) {
        this.$store.commit('offers/SET_PROMO_CODE_DATA', {
          claimCode: this.$route.query.claimCode,
          claimType: this.$route.query.claimType
        });

        this.$router.push({
          query: {
            claimCode: undefined,
            claimType: undefined,
            utm_source: undefined
          }
        });

        if (this.currentUser.accountId) {
          await this.$store.dispatch('offers/claimPromoCode');

          if (this.session.signInRedirectRoute) {
            this.$router.push({ name: this.session.signInRedirectRoute.name });
          }
        }
        else {
          this.$openModal('signIn');
        }
      }
    },

    async checkIsRetroClaimQuery() {
      if (this.$route.query.rct) {
        this.$store.commit('offers/SET_RETRO_RECEIPT_TOKEN', this.$route.query.rct);

        this.$router.push({
          path: this.$route.fullPath,
          query: {
            rct: undefined
          }
        });

        if (this.currentUser.accountId) {
          const retroClaimReceipt = this.offers.retroReceiptToken;

          if (retroClaimReceipt) {
            const dispatchPayload = { type: 'cfReceiptToken', value: retroClaimReceipt };

            await this.$store.dispatch('offers/claimPreviousOrder', dispatchPayload);
          }
        }
      }
    },

    handlePickupLocationOrderType() {
      const pickupType = this.selectedLocation.pickupFulfillmentTypes.find(type => type.isDefaultPickupType)?.value || 'take-out';

      this.$store.dispatch('session/updateOrderDetails', {
        ...this.orderDetails,
        pickupType
      });
    },

    destroyedOrderAheadHandler() {
      events.off('group-ordering:fulfilled', this.groupOrderFulfilledHandler);
    },

    groupOrderFulfilledHandler(order) {
      this.$closeAllModals();

      this.$router.push({
        name: 'orderReceipt',
        params: {
          merchantId: this.$route.params.merchantId,
          receiptToken: 'group'
        },
        query: {
          guestId: order.groupOrder.you.guestId,
          orderId: order.orderId
        }
      });
    },

    modalWatcher(newVal) {
      const modalName = newVal?.name;

      const isNotUserAuthTypeModal = !['signIn', 'createAccount', 'twoFactorAuthModal'].includes(modalName);

      const hasNoOpenModals = !modalName;

      if (hasNoOpenModals && isNotUserAuthTypeModal) {
        this.$store.commit('offers/SET_GIFTING_POINTS_DATA', {
          pointGiftClaimCode: null,
          claimGiftedPointsFlow: null,
          currencyPluralName: null
        });
      }
    }
  }
};
